import * as React from 'react';
import { Link, useParams } from 'react-router-dom';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import CssBaseline from '@mui/material/CssBaseline';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import CardGiftcardOutlinedIcon from '@mui/icons-material/CardGiftcardOutlined';

import { TRUE_DARK, TRUE_MUTED } from '../auth/AuthLayout';
import { currency } from '../ui';
import { BrandColors, DEFAULT_BRAND, PublicBrand, fontStack, useFontsStylesheet } from './brand';
import { CartLine, CartOption, Ordering, useCart } from './cart';
import CartDialog from './CartDialog';
import { Promotion, promoBadge, promoUntil } from './quote';
import { rememberCard, savedCard } from './savedCard';

type Business = { slug: string; name: string; logo: string | null; brand?: PublicBrand };
type MenuMedia = { kind: 'image' | 'video'; url: string; alt: string };
type MenuOptionGroup = { id?: number; name: string; min_select: number; max_select: number | null; options: { id?: number; name: string; price_delta: number; is_default: boolean }[] };
export type MenuProduct = { id: number; name: string; description: string; price: number; sold_out: boolean; coming_soon?: boolean; categories: number[]; media: MenuMedia[]; modifier_groups?: MenuOptionGroup[] };

const choiceHint = (g: MenuOptionGroup) =>
  g.max_select === 1 ? (g.min_select >= 1 ? 'Elige 1' : 'Opcional') : g.max_select == null ? 'Las que quieras' : `Hasta ${g.max_select}`;
export type Menu = {
  business: Business; categories: { id: number; name: string }[]; products: MenuProduct[]; ordering?: Ordering;
  /** The promos that apply by themselves, running now (they apply to orders too). */
  promotions?: Promotion[];
};

type Theme = { c: BrandColors; heading: string; body: string; headingStretch: string; bodyStretch: string };

const themeOf = (brand: PublicBrand): Theme => ({
  c: brand.colors,
  heading: fontStack(brand.fonts.heading),
  body: fontStack(brand.fonts.body),
  headingStretch: brand.fonts.heading.stretch ?? 'normal',
  bodyStretch: brand.fonts.body.stretch ?? 'normal',
});

const ThemeContext = React.createContext<Theme>(themeOf(DEFAULT_BRAND));
const useTheme = () => React.useContext(ThemeContext);

const SOLD_OUT = '#C0392B';

const initials = (name: string) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map(word => word[0]).join('').toUpperCase();

/** Products grouped as the catalog shows them: by category (a product can be in several), the rest under "Otros". */
const sectionsOf = (menu: Menu, label: string) => {
  const sections = menu.categories
    .map(category => ({ id: `c${category.id}`, name: category.name, products: menu.products.filter(p => p.categories.includes(category.id)) }))
    .filter(section => section.products.length > 0);
  const loose = menu.products.filter(p => p.categories.length === 0);
  if (loose.length) sections.push({ id: 'otros', name: sections.length ? 'Otros' : label, products: loose });
  return sections;
};

/** A photo or video. Cards crop it to fill (`cover`); the product view shows it whole. Videos play silently
 * on a loop, without controls on top of them. */
const Media = ({ media, cover }: { media: MenuMedia; cover?: boolean }) =>
  media.kind === 'image'
    ? <Box component="img" src={media.url} alt={media.alt} loading="lazy" sx={{ width: '100%', height: '100%', objectFit: cover ? 'cover' : 'contain', display: 'block' }} />
    : (
      <Box
        component="video"
        src={media.url}
        muted
        loop
        autoPlay
        playsInline
        preload={cover ? 'metadata' : 'auto'}
        aria-label={media.alt}
        sx={{ width: '100%', height: '100%', objectFit: cover ? 'cover' : 'contain', display: 'block' }}
      />
    );

/** The product's photos and videos, one at a time, whole; dots show how many there are and which one is on. */
const MediaCarousel = ({ media, background }: { media: MenuMedia[]; background: string }) => {
  const track = React.useRef<HTMLDivElement>(null);
  const [active, setActive] = React.useState(0);
  const go = (index: number) => {
    const el = track.current;
    if (el) el.scrollTo({ left: index * el.clientWidth, behavior: 'smooth' });
  };
  return (
    <Box sx={{ position: 'relative', bgcolor: background }}>
      <Box
        ref={track}
        onScroll={e => {
          const el = e.currentTarget;
          setActive(Math.round(el.scrollLeft / Math.max(el.clientWidth, 1)));
        }}
        sx={{ display: 'flex', overflowX: 'auto', scrollSnapType: 'x mandatory', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}
      >
        {media.map((item, i) => (
          <Box key={i} sx={{ flex: '0 0 100%', height: { xs: '52vh', sm: 440 }, scrollSnapAlign: 'start' }}>
            <Media media={item} />
          </Box>
        ))}
      </Box>
      {media.length > 1 && (
        <Box role="tablist" aria-label="Fotos y videos"
          sx={{ position: 'absolute', left: '50%', bottom: 12, transform: 'translateX(-50%)', display: 'flex', gap: 0.75, px: 1.25, py: 0.75, borderRadius: 99, bgcolor: 'rgba(0,0,0,0.32)' }}>
          {media.map((item, i) => (
            <Box key={i} component="button" type="button" role="tab" aria-selected={i === active}
              aria-label={`${item.kind === 'video' ? 'Video' : 'Foto'} ${i + 1} de ${media.length}`}
              onClick={() => go(i)}
              sx={{ all: 'unset', cursor: 'pointer', height: 7, width: i === active ? 20 : 7, borderRadius: 99, bgcolor: i === active ? '#fff' : 'rgba(255,255,255,0.6)', transition: 'width .2s' }} />
          ))}
        </Box>
      )}
    </Box>
  );
};

const SoldOut = () => (
  <Chip size="small" label="Agotado" sx={{ bgcolor: 'rgba(192,57,43,0.1)', color: SOLD_OUT, fontWeight: 600, height: 22, fontFamily: 'inherit' }} />
);

/** "Agotado" for the day, or "Próximamente" for what isn't sold yet. */
const Availability = ({ product }: { product: MenuProduct }) =>
  product.coming_soon
    ? <Chip size="small" label="Próximamente" variant="outlined" sx={{ fontWeight: 600, height: 22, fontFamily: 'inherit' }} />
    : product.sold_out ? <SoldOut /> : null;

const unavailable = (product: MenuProduct) => product.sold_out || !!product.coming_soon;

/** A promo's badge on its product ("−15%"), with what it gives on hover. */
const PromoBadge = ({ promo }: { promo: Promotion }) => {
  const t = useTheme();
  return (
    <Chip size="small" label={promoBadge(promo)} title={`${promo.name}: ${promo.label}`} data-testid="promo-badge"
      sx={{ bgcolor: t.c.accent, color: t.c.on_accent, fontWeight: 700, height: 22, fontFamily: 'inherit' }} />
  );
};

/** The business's promos at the top of the menu: what each gives and until when. */
const Promotions = ({ promotions }: { promotions: Promotion[] }) => {
  const t = useTheme();
  return (
    <Box component="section" aria-label="Promociones" data-testid="menu-promotions" sx={{ mb: 4 }}>
      <Typography component="h2" sx={{ fontWeight: 800, color: t.c.text, fontSize: 20, mb: 1.5, fontFamily: t.heading, fontStretch: t.headingStretch, display: 'flex', alignItems: 'center', gap: 1 }}>
        <LocalOfferOutlinedIcon sx={{ fontSize: 20, color: t.c.primary }} />
        Promociones
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: promotions.length > 1 ? '1fr 1fr' : '1fr' }, gap: 1.5 }}>
        {promotions.map(promo => (
          <Box key={promo.id} sx={{ bgcolor: t.c.accent, color: t.c.on_accent, borderRadius: '16px', p: 2 }}>
            <Typography sx={{ fontWeight: 800, fontSize: 16, fontFamily: t.heading, fontStretch: t.headingStretch }}>{promo.name}</Typography>
            <Typography sx={{ fontSize: 14, mt: 0.5, fontFamily: 'inherit' }}>{promo.label}</Typography>
            {promo.description && <Typography sx={{ fontSize: 13.5, mt: 0.5, opacity: 0.85, fontFamily: 'inherit' }}>{promo.description}</Typography>}
            <Typography sx={{ fontSize: 12.5, mt: 1, opacity: 0.8, fontFamily: 'inherit' }}>{promoUntil(promo)} · también en pedidos en línea</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const ProductCard = ({ product, promo, onOpen }: { product: MenuProduct; promo?: Promotion; onOpen?: () => void }) => {
  const t = useTheme();
  return (
    <Box
      component="button"
      type="button"
      onClick={onOpen}
      sx={{
        all: 'unset', boxSizing: 'border-box', cursor: onOpen ? 'pointer' : 'default', display: 'flex', gap: 2, alignItems: 'stretch', width: '100%',
        bgcolor: t.c.surface, border: `1px solid ${t.c.border}`, borderRadius: '16px', p: 2, minHeight: 112,
        opacity: unavailable(product) ? 0.6 : 1, transition: 'box-shadow .15s, border-color .15s',
        '&:hover': onOpen ? { borderColor: t.c.primary, boxShadow: '0 6px 20px rgba(0,0,0,0.06)' } : undefined,
        '&:focus-visible': { outline: `2px solid ${t.c.primary}`, outlineOffset: 2 },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <Typography sx={{ fontWeight: 600, color: t.c.text, fontSize: 16, lineHeight: 1.3, fontFamily: t.body, fontStretch: t.bodyStretch }}>{product.name}</Typography>
        {product.description && (
          <Typography sx={{
            color: t.c.muted, fontSize: 13.5, mt: 0.5, lineHeight: 1.45, fontFamily: t.body, fontStretch: t.bodyStretch,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>
            {product.description}
          </Typography>
        )}
        <Box sx={{ mt: 'auto', pt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontWeight: 700, color: t.c.text, fontSize: 15, fontFamily: t.body, fontStretch: t.bodyStretch }}>{currency(product.price)}</Typography>
          {promo && !unavailable(product) && <PromoBadge promo={promo} />}
          <Availability product={product} />
        </Box>
      </Box>
      {product.media[0] && (
        <Box sx={{ width: 96, height: 96, borderRadius: '12px', overflow: 'hidden', flexShrink: 0, alignSelf: 'center', bgcolor: t.c.background, filter: unavailable(product) ? 'grayscale(1)' : 'none' }}>
          <Media media={product.media[0]} cover />
        </Box>
      )}
    </Box>
  );
};

/** The options chosen in each group (defaults first), and whether every group has what it needs. */
const usePicks = (product: MenuProduct | null) => {
  const [picks, setPicks] = React.useState<Record<string, number[]>>({});
  const [quantity, setQuantity] = React.useState(1);
  React.useEffect(() => {
    setQuantity(1);
    setPicks(Object.fromEntries((product?.modifier_groups ?? []).map(g => [g.name, g.options.filter(o => o.is_default && o.id != null).map(o => o.id!)])));
  }, [product]);
  const toggle = (group: MenuOptionGroup, id: number) => setPicks(current => {
    const chosen = current[group.name] ?? [];
    if (chosen.includes(id)) return { ...current, [group.name]: chosen.filter(x => x !== id) };
    if (group.max_select === 1) return { ...current, [group.name]: [id] };
    if (group.max_select != null && chosen.length >= group.max_select) return current;
    return { ...current, [group.name]: [...chosen, id] };
  });
  const groups = product?.modifier_groups ?? [];
  const missing = groups.find(g => (picks[g.name] ?? []).length < g.min_select);
  const options: CartOption[] = groups.flatMap(g => g.options.filter(o => o.id != null && (picks[g.name] ?? []).includes(o.id))
    .map(o => ({ id: o.id!, name: o.name, price_delta: o.price_delta, group: g.name })));
  const unit = Math.round(((product?.price ?? 0) + options.reduce((sum, o) => sum + o.price_delta, 0)) * 100) / 100;
  return { picks, toggle, quantity, setQuantity, missing, options, unit };
};

const ProductDialog = ({ product, onClose, brand, onAdd }: {
  product: MenuProduct | null; onClose: () => void; brand: PublicBrand;
  /** The business takes orders: options are chosen and the product goes to the cart. */
  onAdd?: (line: Omit<CartLine, 'key'>) => void;
}) => {
  const t = themeOf(brand);
  const fullScreen = useMediaQuery('(max-width:600px)');
  const { picks, toggle, quantity, setQuantity, missing, options, unit } = usePicks(product);
  const orderable = !!onAdd && !!product && !unavailable(product);
  return (
    <Dialog open={Boolean(product)} onClose={onClose} fullScreen={fullScreen} maxWidth="sm" fullWidth
      slotProps={{ paper: { sx: { borderRadius: fullScreen ? 0 : '20px', overflow: 'hidden', bgcolor: t.c.surface } } }}>
      {product && (
        <Box sx={{ position: 'relative', fontFamily: t.body, fontStretch: t.bodyStretch }}>
          <IconButton onClick={onClose} aria-label="Cerrar"
            sx={{ position: 'absolute', top: 12, right: 12, zIndex: 1, bgcolor: 'rgba(255,255,255,0.92)', '&:hover': { bgcolor: '#fff' } }}>
            <CloseIcon />
          </IconButton>
          {product.media.length > 0 && <MediaCarousel media={product.media} background={t.c.background} />}
          <Box sx={{ p: 3, pt: product.media.length ? 2.5 : 7 }}>
            <Typography component="h2" sx={{ fontWeight: 800, fontSize: 22, color: t.c.text, lineHeight: 1.25, fontFamily: t.heading, fontStretch: t.headingStretch }}>{product.name}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <Typography sx={{ fontWeight: 700, fontSize: 18, color: t.c.text, fontFamily: 'inherit' }}>{currency(product.price)}</Typography>
              <Availability product={product} />
            </Box>
            {product.description && (
              <Typography sx={{ color: t.c.muted, mt: 2, whiteSpace: 'pre-line', lineHeight: 1.6, fontFamily: 'inherit' }}>{product.description}</Typography>
            )}
            {(product.modifier_groups ?? []).map(group => (
              <Box key={group.name} sx={{ mt: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography sx={{ fontWeight: 700, color: t.c.text, fontFamily: 'inherit' }}>{group.name}</Typography>
                  <Typography sx={{ color: t.c.muted, fontSize: 12, fontFamily: 'inherit' }}>{choiceHint(group)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                  {group.options.map(option => {
                    const chosen = orderable && option.id != null && (picks[group.name] ?? []).includes(option.id);
                    return (
                      <Chip key={option.name} size="small" variant={chosen ? 'filled' : 'outlined'}
                        label={`${option.name}${option.price_delta ? ` +${currency(option.price_delta)}` : ''}`}
                        onClick={orderable && option.id != null ? () => toggle(group, option.id!) : undefined}
                        sx={{ borderColor: chosen ? t.c.primary : t.c.border, bgcolor: chosen ? t.c.primary : 'transparent',
                          color: chosen ? t.c.on_primary : t.c.text, fontFamily: 'inherit', fontWeight: chosen ? 600 : 400,
                          '&:hover': chosen ? { bgcolor: t.c.primary } : undefined }} />
                    );
                  })}
                </Box>
              </Box>
            ))}
          </Box>
          {orderable && (
            <Box sx={{ position: 'sticky', bottom: 0, p: 2, pt: 1.5, bgcolor: t.c.surface, borderTop: `1px solid ${t.c.border}`, display: 'flex', gap: 1.5, alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', border: `1px solid ${t.c.border}`, borderRadius: 99 }}>
                <IconButton size="small" aria-label="Uno menos" onClick={() => setQuantity(q => Math.max(1, q - 1))} sx={{ color: t.c.text }}><RemoveIcon fontSize="small" /></IconButton>
                <Typography sx={{ minWidth: 24, textAlign: 'center', fontWeight: 700, fontFamily: 'inherit' }}>{quantity}</Typography>
                <IconButton size="small" aria-label="Uno más" onClick={() => setQuantity(q => Math.min(99, q + 1))} sx={{ color: t.c.text }}><AddIcon fontSize="small" /></IconButton>
              </Box>
              <Button fullWidth variant="contained" disabled={!!missing} data-testid="add-to-cart"
                onClick={() => { onAdd!({ product: product!.id, name: product!.name, unit, options, quantity }); onClose(); }}
                sx={{ py: 1.25, borderRadius: 99, textTransform: 'none', fontWeight: 700, fontFamily: 'inherit', bgcolor: t.c.primary, color: t.c.on_primary,
                  boxShadow: 'none', '&:hover': { bgcolor: t.c.primary, opacity: 0.9, boxShadow: 'none' } }}>
                {missing ? `Elige ${missing.name.toLowerCase()}` : `Agregar · ${currency(unit * quantity)}`}
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Dialog>
  );
};

// Pixel strings: MUI reads width: 1 as 100%.
const visuallyHidden = { position: 'absolute', width: '1px', height: '1px', margin: '-1px', overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' } as const;

/**
 * The menu in the business's brand. The public page renders it full screen; the dashboard renders it
 * inside a phone-sized frame (`embedded`) to preview a brand before saving it.
 */
export const MenuView = ({ menu, brand, embedded, onOpenProduct, notice }: {
  menu: Menu; brand: PublicBrand; embedded?: boolean; onOpenProduct?: (product: MenuProduct) => void;
  /** Something for this customer above the menu (the rewards on their card). */
  notice?: React.ReactNode;
}) => {
  const t = themeOf(brand);
  const business = menu.business;
  const label = brand.catalog_label || 'Menú';
  const sections = React.useMemo(() => sectionsOf(menu, label), [menu, label]);
  const promotions = React.useMemo(() => menu.promotions ?? [], [menu.promotions]);
  // The badge on each product: its first promo.
  const promoOf = React.useMemo(() => {
    const map = new Map<number, Promotion>();
    for (const promo of promotions) if (promo.product != null && !map.has(promo.product)) map.set(promo.product, promo);
    return map;
  }, [promotions]);
  const scrollTo = (id: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById(`${embedded ? 'preview-' : ''}${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <ThemeContext.Provider value={t}>
      <Box sx={{ minHeight: embedded ? '100%' : '100vh', bgcolor: t.c.background, color: t.c.text, fontFamily: t.body, fontStretch: t.bodyStretch }}>
        {brand.cover && (
          <Box component="img" src={brand.cover} alt="" sx={{ display: 'block', width: '100%', height: embedded ? 150 : { xs: 190, sm: 300 }, objectFit: 'cover' }} />
        )}
        <Box component="header" sx={{ bgcolor: t.c.surface, borderBottom: `1px solid ${t.c.border}` }}>
          <Box sx={{ maxWidth: 960, mx: 'auto', px: 2, py: embedded ? 2.5 : { xs: 3, sm: 4 }, display: 'flex', alignItems: 'center', gap: 2.5 }}>
            {brand.logo ? (
              <Box component="img" src={brand.logo} alt={business.name}
                sx={{ height: embedded ? 72 : { xs: 84, sm: 104 }, maxWidth: 220, objectFit: 'contain', flexShrink: 0 }} />
            ) : (
              <Box sx={{ width: 64, height: 64, borderRadius: '16px', display: 'grid', placeItems: 'center', flexShrink: 0, bgcolor: t.c.primary }}>
                <Typography sx={{ color: t.c.on_primary, fontWeight: 700, fontSize: 22, fontFamily: t.heading, fontStretch: t.headingStretch }}>{initials(business.name)}</Typography>
              </Box>
            )}
            <Box sx={{ minWidth: 0 }}>
              {/* With a logo the name is in the image; screen readers and search engines still get it. */}
              <Typography component="h1" sx={{
                ...(brand.logo ? visuallyHidden : {}),
                fontWeight: 800, fontSize: { xs: 22, sm: 28 }, color: t.c.text, lineHeight: 1.2, fontFamily: t.heading, fontStretch: t.headingStretch,
              }}>
                {business.name}
              </Typography>
              <Typography sx={{ color: brand.tagline ? t.c.text : t.c.muted, fontWeight: brand.tagline ? 500 : 400, fontSize: brand.logo ? 17 : 14, mt: brand.logo ? 0 : 0.5, fontFamily: 'inherit' }}>
                {brand.tagline || label}
              </Typography>
            </Box>
          </Box>
        </Box>

        {sections.length > 1 && (
          <Box component="nav" aria-label="Categorías" sx={{ position: 'sticky', top: 0, zIndex: 2, bgcolor: t.c.background, borderBottom: `1px solid ${t.c.border}` }}>
            <Box sx={{ maxWidth: 960, mx: 'auto', px: 2, py: 1.5, display: 'flex', gap: 1, overflowX: 'auto', '&::-webkit-scrollbar': { display: 'none' } }}>
              {sections.map(section => (
                <Chip key={section.id} label={section.name} component="a" href={`#${section.id}`} clickable onClick={scrollTo(section.id)}
                  sx={{
                    bgcolor: t.c.surface, border: `1px solid ${t.c.border}`, fontWeight: 600, color: t.c.text, flexShrink: 0, fontFamily: 'inherit',
                    '&:hover, &:focus-visible': { bgcolor: t.c.primary, color: t.c.on_primary, borderColor: t.c.primary },
                  }} />
              ))}
            </Box>
          </Box>
        )}

        <Box component="main" sx={{ maxWidth: 960, mx: 'auto', px: 2, py: 3 }}>
          {sections.length === 0 && (
            <Box sx={{ bgcolor: t.c.surface, border: `1px solid ${t.c.border}`, borderRadius: '20px', p: { xs: 4, sm: 5 }, textAlign: 'center' }}>
              <Typography sx={{ fontWeight: 700, color: t.c.text, fontSize: 18, fontFamily: t.heading, fontStretch: t.headingStretch }}>Aún no hay productos publicados</Typography>
              <Typography sx={{ color: t.c.muted, mt: 1, fontFamily: 'inherit' }}>Vuelve pronto.</Typography>
            </Box>
          )}
          {notice}
          {promotions.length > 0 && <Promotions promotions={promotions} />}
          {sections.map(section => (
            <Box component="section" key={section.id} id={`${embedded ? 'preview-' : ''}${section.id}`} sx={{ scrollMarginTop: 72, mb: 4 }}>
              <Typography component="h2" sx={{ fontWeight: 800, color: t.c.text, fontSize: 20, mb: 1.5, fontFamily: t.heading, fontStretch: t.headingStretch, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: t.c.accent, flexShrink: 0 }} />
                {section.name}
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: embedded ? '1fr' : { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
                {section.products.map(product => (
                  <ProductCard key={product.id} product={product} promo={promoOf.get(product.id)} onOpen={onOpenProduct ? () => onOpenProduct(product) : undefined} />
                ))}
              </Box>
            </Box>
          ))}
        </Box>

        {/* Staff open the register at receipt.com.mx/<slug>/pos directly; the menu is for customers. */}
        <Box component="footer" sx={{ maxWidth: 960, mx: 'auto', px: 2, pb: 5, pt: 1, display: 'flex', justifyContent: 'center', borderTop: `1px solid ${t.c.border}` }}>
          <Typography component={Link} to="/" sx={{ color: t.c.muted, fontSize: 13, textDecoration: 'none', pt: 2, fontFamily: 'inherit' }}>Hecho con TRUE POS</Typography>
          {brand.privacy_url && (
            <Typography component="a" href={brand.privacy_url} target="_blank" rel="noopener" sx={{ color: t.c.muted, fontSize: 13, pt: 2, ml: 2, fontFamily: 'inherit' }}>
              Aviso de privacidad
            </Typography>
          )}
        </Box>
      </Box>
    </ThemeContext.Provider>
  );
};

/** receipt.com.mx/<slug>: the business's public page with its digital menu, in its brand. Online orders come next. */
const BusinessPage = () => {
  const { slug = '' } = useParams();
  const [menu, setMenu] = React.useState<Menu | null>(null);
  const [state, setState] = React.useState<'loading' | 'found' | 'missing'>('loading');
  const [open, setOpen] = React.useState<MenuProduct | null>(null);
  const cart = useCart(slug);
  const [cartOpen, setCartOpen] = React.useState(false);

  React.useEffect(() => {
    let alive = true;
    setState('loading');
    fetch(`${process.env.REACT_APP_BASE_URL}/api/public/businesses/${encodeURIComponent(slug)}/catalog/`)
      .then(async response => {
        if (!alive) return;
        if (!response.ok) { setState('missing'); return; }
        setMenu(await response.json());
        setState('found');
      })
      .catch(() => { if (alive) setState('missing'); });
    return () => { alive = false; };
  }, [slug]);

  const business = menu?.business;
  const brand = business?.brand ?? DEFAULT_BRAND;
  const ordering = menu?.ordering;
  useFontsStylesheet(brand.fonts_css);

  // Arrived from their card (a business whose "sitio para pedidos" is this page): the card's code comes in the link's #
  // (no server sees it); kept on this phone like opening the card does, and taken out of the address.
  const [cardFromLink] = React.useState(() => {
    const code = new URLSearchParams(window.location.hash.slice(1)).get('tarjeta');
    if (code) {
      rememberCard(slug, code);
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
    return code;
  });

  // Their card open on this phone: the rewards they can use on an order, at the top of the menu.
  const [rewards, setRewards] = React.useState<string[]>([]);
  React.useEffect(() => {
    const code = ordering?.enabled && ordering.rewards ? savedCard(slug) : null;
    if (!code) return;
    let alive = true;
    fetch(`${process.env.REACT_APP_BASE_URL}/api/public/businesses/${encodeURIComponent(slug)}/mi/${encodeURIComponent(code)}/`)
      .then(response => (response.ok ? response.json() : null))
      .then(data => {
        if (alive && data) setRewards((data.rewards ?? []).filter((r: any) => r.state === 'available').map((r: any) => String(r.title)));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [slug, ordering?.enabled, ordering?.rewards, cardFromLink]);

  React.useEffect(() => {
    document.title = business ? `${business.name} · ${business.brand?.catalog_label || 'Menú'}` : 'TRUE POS';
  }, [business]);

  if (state !== 'found' || !menu || !business) {
    return (
      <>
        <CssBaseline />
        <Box sx={{ minHeight: '100vh', bgcolor: '#F2F5F7', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', px: 2, py: 6, fontFamily: 'Inter, sans-serif' }}>
          <Box sx={{ width: '100%', maxWidth: 440, bgcolor: '#fff', borderRadius: '20px', boxShadow: '0 8px 30px rgba(20,37,44,0.08)', p: { xs: 4, sm: 5 }, textAlign: 'center' }}>
            {state === 'loading' && <CircularProgress size={32} sx={{ color: TRUE_DARK, my: 6 }} />}
            {state === 'missing' && (
              <>
                <Typography component="h1" sx={{ fontWeight: 700, fontSize: 24, color: TRUE_DARK }}>No encontramos este negocio</Typography>
                <Typography sx={{ color: TRUE_MUTED, mt: 1.5 }}>
                  Revisa el enlace: <Box component="span" sx={{ fontWeight: 600 }}>receipt.com.mx/{slug}</Box> no corresponde a ningún negocio.
                </Typography>
              </>
            )}
          </Box>
          <Typography component={Link} to="/" sx={{ mt: 3, color: TRUE_MUTED, fontSize: 13, textDecoration: 'none' }}>Hecho con TRUE POS</Typography>
        </Box>
      </>
    );
  }

  return (
    <>
      <CssBaseline />
      <MenuView menu={menu} brand={brand} onOpenProduct={setOpen} notice={rewards.length > 0 && (
        <Box data-testid="menu-rewards" sx={{ mb: 3, p: 2, borderRadius: '16px', display: 'flex', gap: 1.5, alignItems: 'center',
          bgcolor: brand.colors.surface, border: `1.5px dashed ${brand.colors.primary}` }}>
          <CardGiftcardOutlinedIcon sx={{ color: brand.colors.primary }} />
          <Typography sx={{ fontFamily: 'inherit', fontSize: 15 }}>
            <strong>{rewards.length === 1 ? 'Tienes una recompensa' : `Tienes ${rewards.length} recompensas`}:</strong> {rewards.join(', ')}.
            {' '}Úsala{rewards.length === 1 ? '' : 's'} en tu pedido al finalizarlo.
          </Typography>
        </Box>
      )} />
      <ProductDialog product={open} onClose={() => setOpen(null)} brand={brand} onAdd={ordering?.enabled ? cart.add : undefined} />
      {ordering?.enabled && (cart.count > 0 || ordering.free_text) && (
        <Box sx={{ position: 'fixed', left: 0, right: 0, bottom: 0, p: 2, pb: 'calc(16px + env(safe-area-inset-bottom))', display: 'flex', justifyContent: 'center', zIndex: 10, pointerEvents: 'none' }}>
          <Button variant="contained" data-testid="cart-bar" startIcon={<ShoppingBagOutlinedIcon />} onClick={() => setCartOpen(true)}
            sx={{ pointerEvents: 'auto', width: '100%', maxWidth: 520, py: 1.5, borderRadius: 99, textTransform: 'none', fontWeight: 700, fontSize: 16,
              fontFamily: fontStack(brand.fonts.body), bgcolor: brand.colors.primary, color: brand.colors.on_primary,
              boxShadow: '0 8px 24px rgba(0,0,0,0.18)', '&:hover': { bgcolor: brand.colors.primary } }}>
            {cart.count > 0 ? <>Ver mi pedido · {cart.count} · {currency(cart.subtotal)}</> : 'Hacer un pedido'}
          </Button>
        </Box>
      )}
      {ordering?.enabled && (
        <CartDialog open={cartOpen} onClose={() => setCartOpen(false)} slug={slug} businessName={business.name} brand={brand} ordering={ordering} cart={cart}
          promotions={menu.promotions ?? []} />
      )}
    </>
  );
};

export default BusinessPage;
